/* eslint-disable @typescript-eslint/no-explicit-any */
import { registerLocaleData } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Injector, Optional } from '@angular/core';
import { L10nConfig, L10nLoader, L10nLocale, L10nMissingTranslationHandler, L10nProvider, L10nStorage, L10nTranslationLoader, L10nTranslationService, L10nTranslationToken } from 'angular-l10n';
import { Observable } from 'rxjs';

// Required for date/number formatting for angular directives (like DatePipe)
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeKo from '@angular/common/locales/ko';
import localeRu from '@angular/common/locales/ru';
import localeVi from '@angular/common/locales/vi';
import localeTr from '@angular/common/locales/tr';
import localeZh from '@angular/common/locales/zh';

export class LocaleHelper {
  static readonly defaultLocale = 'en-US';

  /** Register the locale data with Angular - called in main.ts */
  static registerNgLocales() {
    registerLocaleData(localeDe);
    registerLocaleData(localeEs);
    registerLocaleData(localeFr);
    registerLocaleData(localeKo);
    registerLocaleData(localeRu);
    registerLocaleData(localeTr);
    registerLocaleData(localeVi);
    registerLocaleData(localeZh);
  }

  /** Must be kept in sync with registerNgLocales() ^^ */
  static readonly supportedNgLocales = [
    'en',
    'de',
    'es',
    'fr',
    'ko',
    'ru',
    'tr',
    'vi',
    'zh',
  ];

  static getSafeNgLocale(locale?: string): string {
    const preferredLocale = locale || navigator.language || LocaleHelper.defaultLocale;
    return LocaleHelper.isNgSupported(preferredLocale) ? preferredLocale : LocaleHelper.defaultLocale;
  }

  /** Returns true if the locale is supported for date/time/number localization by Angular (ex: DatePipe) */
  static isNgSupported(locale: string): boolean {
    if (!locale || locale.length < 2) return false;
    const languageCode = locale.substring(0, 2);
    return this.supportedNgLocales.includes(languageCode);
  }

  /** Returns true if the locale is supported for app string localization by our localization service (ex: L10nTranslatePipe, etc) */
  static isL10nSupported(locale: string): boolean {
    if (!locale || locale.length < 2) return false;
    const languageCode = locale.substring(0, 2);
    const supported = l10nConfig.schema.map(schema => schema.locale.language);
    return supported.includes(languageCode);
  }

  /** Parses the language code from a locale string ('en-US' -> 'en') */
  static parseLanguageCode(locale: string = navigator.language || LocaleHelper.defaultLocale): string {
    // handle different locale formats: en-US, en_US, en
    const parts = locale.replace('_', '-').split('-');
    return parts[0].toLowerCase() || 'en';
  }

  /** Parses the (optional) region code from a locale string ('en-US' -> 'US', 'en' -> undefined) */
  static parseRegionCode(locale: string = navigator.language || LocaleHelper.defaultLocale): string | undefined {
    // handle different locale formats: en-US, en_US, en
    const parts = locale.replace('_', '-').split('-');
    const regionCode = parts[1]?.toUpperCase();

    // infer a default region from language if not provided
    const defaultRegions: Record<string, string> = {
      'en': 'US',
      'tr': 'TR',
      'zh': 'CN',
    };

    return regionCode || defaultRegions[this.parseLanguageCode(locale)];
  }
}

export const l10nConfig: L10nConfig = {
  format: 'language',
  providers: [
    { name: 'app', asset: './locale/messages', options: { version: '9.0.0' } }
  ],
  fallback: true,
  cache: true,
  keySeparator: '.',
  defaultLocale: { language: 'en', currency: 'USD' },
  schema: [
    { locale: { language: 'en', }, dir: 'ltr', text: 'English' },
    { locale: { language: 'de', }, dir: 'ltr', text: 'Deutsch' },
    { locale: { language: 'es', }, dir: 'ltr', text: 'Español' },
    { locale: { language: 'fr', }, dir: 'ltr', text: 'Français' },
    { locale: { language: 'tr', }, dir: 'ltr', text: 'Türkiye' },
    { locale: { language: 'zh', }, dir: 'ltr', text: '中文' },
  ]
};

export function l10nPreload(translation: L10nTranslationService, translationLoader: L10nTranslationLoader): () => Promise<void> {
  return () => new Promise((resolve) => {
    // preload English values for fallback for missing translations
    // see: https://github.com/robisim74/angular-l10n#preloading-data
    const defaultLanguage = l10nConfig.defaultLocale.language;
    translationLoader.get(defaultLanguage, l10nConfig.providers[0])
      .subscribe({
        next: (data) => translation.addData(data, defaultLanguage),
        complete: () => resolve()
      });
  });
}

export function l10nInit(l10nLoader: L10nLoader): () => Promise<void> {
  return () => l10nLoader.init();
}

/**
 * For storing the selected locale in session storage. Supplied in the `L10nTranslationToken`.
 * Useful if/when we decide to implement an explicit locale selection widget, but unused for now.
 */
@Injectable() export class LocaleStorage implements L10nStorage {
  private readonly hasStorage: boolean;

  constructor() {
    this.hasStorage = typeof Storage !== 'undefined';
  }

  public async read(): Promise<L10nLocale | null> {
    if (this.hasStorage) {
      return Promise.resolve(JSON.parse(sessionStorage.getItem('locale')));
    } else {
      return Promise.resolve(null);
    }
  }

  public async write(locale: L10nLocale): Promise<void> {
    if (this.hasStorage) {
      sessionStorage.setItem('locale', JSON.stringify(locale));
    }
  }
}

@Injectable() export class HttpTranslationLoader implements L10nTranslationLoader {
  private readonly headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(@Optional() private readonly http: HttpClient) { }

  public get(language: string, provider: L10nProvider): Observable<{ [key: string]: any }> {
    const url = `${provider.asset}-${language}.json`;
    const providerOptions = provider.options as Record<string, any>;
    const httpOptions = {
      headers: this.headers,
      params: new HttpParams().set('v', providerOptions?.version)
    };
    return this.http.get(url, httpOptions);
  }
}

@Injectable() export class EnMissingTranslationHandler implements L10nMissingTranslationHandler {
  translation?: L10nTranslationService = null;
  constructor(injector: Injector) {
    // use setTimeout() to avoid cyclic dependency error
    setTimeout(() => {
      this.translation = injector.get(L10nTranslationService);
    });
  }

  handle(key: string): any {
    const defaultLanguage = l10nConfig.defaultLocale.language;
    const translation = this.translation;
    if (translation?.has(key, defaultLanguage)) {
      return translation.translate(key, null, defaultLanguage);
    } else {
      return key;
    }
  }
}

export const l10nToken: L10nTranslationToken = {
  storage: undefined, // ROAD-13780 - see `LocaleStorage` above
  translationLoader: HttpTranslationLoader,
  missingTranslationHandler: EnMissingTranslationHandler
}
